import { Injectable, inject } from '@angular/core';
import { LocalStorageService } from 'ngx-webstorage';

export interface TextareaSize {
    height: number;
    timestamp: number;
}

export interface TextareaSizeCache {
    [textareaKey: string]: TextareaSize;
}

@Injectable({
    providedIn: 'root'
})
export class TextareaSizeStorageService {
    private readonly STORAGE_KEY = 'PHX_TEXTAREA_SIZES';
    private readonly MAX_ENTRIES = 99;
    private localStorageService = inject(LocalStorageService);

    private readonly MIN_HEIGHT_PX = 56;
    private readonly MAX_HEIGHT_VIEWPORT_RATIO = 0.25;
    /** Aligns with AppLayoutService.isMobile() (isDesktop = width > 991). */
    private readonly MOBILE_MAX_WIDTH_PX = 991;

    /**
     * Get the saved height for a specific textarea key
     */
    getSize(textareaKey: string): TextareaSize | null {
        if (!textareaKey) return null;
        try {
            const cache = this.getCache();
            return cache[textareaKey] || null;
        } catch (error) {
            console.warn('Error reading textarea size from storage:', error);
            return null;
        }
    }

    /**
     * Get saved height for restore. Clamps only on mobile viewports (width <= 991px).
     * Desktop returns raw stored height. Storage value is never modified.
     */
    getClampedHeight(
        textareaKey: string,
        viewportHeight = window.innerHeight,
        viewportWidth = window.innerWidth
    ): number | null {
        const entry = this.getSize(textareaKey);
        if (!entry) {
            return null;
        }
        if (viewportWidth > this.MOBILE_MAX_WIDTH_PX) {
            return entry.height;
        }
        const maxHeight = Math.round(viewportHeight * this.MAX_HEIGHT_VIEWPORT_RATIO);
        return Math.max(this.MIN_HEIGHT_PX, Math.min(entry.height, maxHeight));
    }

    /**
     * Save the height for a specific textarea key
     * Implements LRU eviction when cache exceeds MAX_ENTRIES
     */
    saveSize(textareaKey: string, height: number): void {
        if (!textareaKey || !height) return;
        try {
            const cache = this.getCache();
            cache[textareaKey] = {
                height,
                timestamp: Date.now()
            };
            const entries = Object.entries(cache);
            if (entries.length > this.MAX_ENTRIES) {
                entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
                const entriesToRemove = entries.length - this.MAX_ENTRIES;
                for (let i = 0; i < entriesToRemove; i++) {
                    delete cache[entries[i][0]];
                }
            }
            this.setCache(cache);
        } catch (error) {
            console.warn('Error saving textarea size to storage:', error);
        }
    }

    /**
     * Clear the height for a specific textarea key
     */
    clearSize(textareaKey: string): void {
        if (!textareaKey) return;

        try {
            const cache = this.getCache();
            delete cache[textareaKey];
            this.setCache(cache);
        } catch (error) {
            console.warn('Error clearing textarea size from storage:', error);
        }
    }

    /**
     * Clear all saved textarea sizes
     */
    clearAll(): void {
        try {
            this.localStorageService.clear(this.STORAGE_KEY);
        } catch (error) {
            console.warn('Error clearing all textarea sizes from storage:', error);
        }
    }

    private getCache(): TextareaSizeCache {
        try {
            const cache = this.localStorageService.retrieve(this.STORAGE_KEY);
            if (!cache || typeof cache !== 'object') {
                return {};
            }
            const validatedCache: TextareaSizeCache = {};
            for (const [key, value] of Object.entries(cache)) {
                if (this.isValidSize(value)) {
                    validatedCache[key] = value as TextareaSize;
                }
            }
            return validatedCache;
        } catch (error) {
            console.warn('Error reading textarea size cache, returning empty cache:', error);
            return {};
        }
    }

    private setCache(cache: TextareaSizeCache): void {
        try {
            this.localStorageService.store(this.STORAGE_KEY, cache);
        } catch (error) {
            console.error('Error saving textarea size cache:', error);
            if (error instanceof Error && error.name === 'QuotaExceededError') {
                console.warn('localStorage quota exceeded, clearing textarea size cache');
                this.clearAll();
            }
        }
    }

    private isValidSize(value: unknown): value is TextareaSize {
        return (
            value !== null &&
            typeof value === 'object' &&
            typeof (value as TextareaSize).height === 'number' &&
            typeof (value as TextareaSize).timestamp === 'number' &&
            (value as TextareaSize).height > 0
        );
    }

    getStats(): { entryCount: number; oldestEntry: number; newestEntry: number } {
        const cache = this.getCache();
        const entries = Object.values(cache);
        if (entries.length === 0) {
            return { entryCount: 0, oldestEntry: 0, newestEntry: 0 };
        }
        const timestamps = entries.map(e => e.timestamp);
        return {
            entryCount: entries.length,
            oldestEntry: Math.min(...timestamps),
            newestEntry: Math.max(...timestamps)
        };
    }
}
